import { Fragment, PureComponent, ReactNode } from 'react';
import { Teeth, TeethToSurfacesNames, teethToSurfacesIds, teethToSurfacesNames } from './constants';
import { DentalExamination as DentalExaminationType } from '../../../../store/types/prescription';
import { connect } from 'react-redux';
import {
    getChartType,
    getDentalExaminations,
    getProcessedDentalExaminations,
} from '../../../../store/selectors/prescriptionPad';
import { GlobalState } from '../../../../store/reducers';
import { ProcessedDentalExaminations } from './DentalExamination';
import { ChartType } from './DentalChart';
import ToothComponent from './DentalExamination/ToothComponent';

export interface KeyToSetObject {
    [k: string]: Set<string>;
}

interface KeyToArrayObject {
    [k: string]: string[];
}

interface Props {
    dentalExaminations?: DentalExaminationType[];
    selectTeeth: (teeth: string) => void;
    selectSurface: (teeth: string, surface: number) => void;
    processedDentalExaminations: ProcessedDentalExaminations[];
    chartType: ChartType;
    upperTeethToShow: Teeth[];
    lowerTeethToShow: Teeth[];
    selectedMultiTeeth: KeyToSetObject;
    selectMultiTeeth: (teeth: string, surface?: number) => void;
    isSelectedMultiTeethArray: boolean;
}

interface State {
    teethAndSurfacesObject: KeyToSetObject;
    teethAndExaminations: KeyToArrayObject;
    currentTeethToSurfacesNames: TeethToSurfacesNames;
    currentTeethToSurfacesIds: TeethToSurfacesNames;
}

const getTeethAndSurfacesObject = (
    dentalExaminations?: DentalExaminationType[],
    processedDentalExaminations?: ProcessedDentalExaminations[],
    selectedMultiTeeth?: KeyToSetObject,
    isSelectedMultiTeethArray?: boolean,
): KeyToSetObject => {
    const teethAndSurfacesObject: KeyToSetObject = {};
    if (isSelectedMultiTeethArray) {
        for (const key in selectedMultiTeeth) {
            if (teethAndSurfacesObject[key]) {
                const newData = teethAndSurfacesObject[key];
                const surfaces = selectedMultiTeeth[key];
                Array.from(surfaces).map((i) => {
                    newData.add(i);
                });
            } else {
                const surfaces = selectedMultiTeeth[key];
                teethAndSurfacesObject[key] = new Set(Array.from(surfaces));
            }
        }
        return teethAndSurfacesObject;
    }
    dentalExaminations?.forEach((i) => {
        if (teethAndSurfacesObject[i.teeth_id || '']) {
            const newData = teethAndSurfacesObject[i.teeth_id || ''];
            i.surfaces?.forEach((i) => {
                newData.add(i.id);
            });
        } else {
            teethAndSurfacesObject[i.teeth_id || ''] = new Set(i.surfaces?.map((i) => i.id));
        }
    });
    processedDentalExaminations?.forEach((i) => {
        if (i.group) {
            const groupArray = Object.entries(i.group.data);
            groupArray.forEach(([teeth, surfaces]) => {
                if (teethAndSurfacesObject[teeth || '']) {
                    const newData = teethAndSurfacesObject[teeth || ''];
                    surfaces?.forEach((i) => {
                        newData.add(i);
                    });
                } else {
                    teethAndSurfacesObject[teeth || ''] = new Set(i.group?.data[teeth]);
                }
            });
        } else {
            if (teethAndSurfacesObject[i.teeth || '']) {
                const newData = teethAndSurfacesObject[i.teeth || ''];
                i.surfaces?.forEach((i) => {
                    newData.add(i);
                });
            } else {
                teethAndSurfacesObject[i.teeth || ''] = new Set(i.surfaces);
            }
        }
    });
    return teethAndSurfacesObject;
};

const getTeethAndExaminations = (
    dentalExaminations?: DentalExaminationType[],
): KeyToArrayObject => {
    const teethAndExaminationsObject: KeyToArrayObject = {};
    dentalExaminations?.forEach((i) => {
        if (teethAndExaminationsObject[i.teeth_id || '']) {
            teethAndExaminationsObject[i.teeth_id || ''].push(i.id);
        } else {
            teethAndExaminationsObject[i.teeth_id || ''] = [i.id];
        }
    });
    return teethAndExaminationsObject;
};

class Chart extends PureComponent<Props, State> {
    state: State = {
        teethAndSurfacesObject: {},
        teethAndExaminations: {},
        currentTeethToSurfacesNames: {},
        currentTeethToSurfacesIds: {},
    };

    static getDerivedStateFromProps(props: Props): Partial<State> {
        const {
            chartType,
            dentalExaminations,
            processedDentalExaminations,
            selectedMultiTeeth,
            isSelectedMultiTeethArray,
        } = props;
        const teethAndSurfacesObject = getTeethAndSurfacesObject(
            dentalExaminations,
            processedDentalExaminations,
            selectedMultiTeeth,
            isSelectedMultiTeethArray,
        );
        const teethAndExaminations = getTeethAndExaminations(dentalExaminations);
        const currentTeethToSurfacesNames = teethToSurfacesNames(chartType);
        const currentTeethToSurfacesIds = teethToSurfacesIds(chartType);
        return {
            teethAndSurfacesObject,
            teethAndExaminations,
            currentTeethToSurfacesNames,
            currentTeethToSurfacesIds,
        };
    }

    render(): ReactNode {
        const {
            selectTeeth,
            selectSurface,
            chartType,
            upperTeethToShow,
            lowerTeethToShow,
            selectedMultiTeeth,
            selectMultiTeeth,
            isSelectedMultiTeethArray,
        } = this.props;
        const {
            teethAndSurfacesObject,
            teethAndExaminations,
            currentTeethToSurfacesNames,
            currentTeethToSurfacesIds,
        } = this.state;
        const isNoTeethFound = upperTeethToShow.length === 0 && lowerTeethToShow.length === 0; // nc

        return (
            <Fragment>
                {upperTeethToShow.length > 0 && (
                    <div className="flex justify-center overflow-hidden select-none">
                        <div className="flex items-center overflow-x-auto py-5 overflow-y-hidden">
                            {upperTeethToShow.map(({ tooth }, index) => (
                                <ToothComponent
                                    tooth={tooth}
                                    selectTeeth={selectTeeth}
                                    examinations={teethAndExaminations[tooth]}
                                    isSelected={Boolean(teethAndSurfacesObject[tooth])}
                                    selectSurface={selectSurface}
                                    firstSurfaceName={currentTeethToSurfacesNames[tooth][1]}
                                    secondSurfaceName={currentTeethToSurfacesNames[tooth][2]}
                                    thirdSurfaceName={currentTeethToSurfacesNames[tooth][3]}
                                    fourthSurfaceName={currentTeethToSurfacesNames[tooth][4]}
                                    fifthSurfaceName={currentTeethToSurfacesNames[tooth][5]}
                                    sixthSurfaceName={currentTeethToSurfacesNames[tooth][6]}
                                    seventhSurfaceName={currentTeethToSurfacesNames[tooth][7]}
                                    isFirstSurfaceSelected={teethAndSurfacesObject[tooth]?.has(
                                        currentTeethToSurfacesIds[tooth][1],
                                    )}
                                    isSecondSurfaceSelected={teethAndSurfacesObject[tooth]?.has(
                                        currentTeethToSurfacesIds[tooth][2],
                                    )}
                                    isThirdSurfaceSelected={teethAndSurfacesObject[tooth]?.has(
                                        currentTeethToSurfacesIds[tooth][3],
                                    )}
                                    isFourthSurfaceSelected={teethAndSurfacesObject[tooth]?.has(
                                        currentTeethToSurfacesIds[tooth][4],
                                    )}
                                    isFifthSurfaceSelected={teethAndSurfacesObject[tooth]?.has(
                                        currentTeethToSurfacesIds[tooth][5],
                                    )}
                                    isSixthSurfaceSelected={teethAndSurfacesObject[tooth]?.has(
                                        currentTeethToSurfacesIds[tooth][6],
                                    )}
                                    isSeventhSurfaceSelected={teethAndSurfacesObject[tooth]?.has(
                                        currentTeethToSurfacesIds[tooth][7],
                                    )}
                                    selectedMultiTeeth={selectedMultiTeeth}
                                    selectMultiTeeth={selectMultiTeeth}
                                    isSelectedMultiTeethArray={isSelectedMultiTeethArray}
                                    showRightBorder={index === upperTeethToShow.length / 2 - 1}
                                />
                            ))}
                        </div>
                    </div>
                )}
                {upperTeethToShow.length > 0 && lowerTeethToShow.length > 0 && (
                    <p className="border-b border-darwin-neutral_new-200"></p>
                )}
                {lowerTeethToShow.length > 0 && (
                    <div className="flex justify-center overflow-hidden select-none">
                        <div className="flex items-center overflow-x-auto py-5 overflow-y-hidden">
                            {lowerTeethToShow.map(({ tooth }, index) => (
                                <ToothComponent
                                    tooth={tooth}
                                    selectTeeth={selectTeeth}
                                    examinations={teethAndExaminations[tooth]}
                                    isSelected={Boolean(teethAndSurfacesObject[tooth])}
                                    selectSurface={selectSurface}
                                    firstSurfaceName={currentTeethToSurfacesNames[tooth][1]}
                                    secondSurfaceName={currentTeethToSurfacesNames[tooth][2]}
                                    thirdSurfaceName={currentTeethToSurfacesNames[tooth][3]}
                                    fourthSurfaceName={currentTeethToSurfacesNames[tooth][4]}
                                    fifthSurfaceName={currentTeethToSurfacesNames[tooth][5]}
                                    sixthSurfaceName={currentTeethToSurfacesNames[tooth][6]}
                                    seventhSurfaceName={currentTeethToSurfacesNames[tooth][7]}
                                    isFirstSurfaceSelected={teethAndSurfacesObject[tooth]?.has(
                                        currentTeethToSurfacesIds[tooth][1],
                                    )}
                                    isSecondSurfaceSelected={teethAndSurfacesObject[tooth]?.has(
                                        currentTeethToSurfacesIds[tooth][2],
                                    )}
                                    isThirdSurfaceSelected={teethAndSurfacesObject[tooth]?.has(
                                        currentTeethToSurfacesIds[tooth][3],
                                    )}
                                    isFourthSurfaceSelected={teethAndSurfacesObject[tooth]?.has(
                                        currentTeethToSurfacesIds[tooth][4],
                                    )}
                                    isFifthSurfaceSelected={teethAndSurfacesObject[tooth]?.has(
                                        currentTeethToSurfacesIds[tooth][5],
                                    )}
                                    isSixthSurfaceSelected={teethAndSurfacesObject[tooth]?.has(
                                        currentTeethToSurfacesIds[tooth][6],
                                    )}
                                    isSeventhSurfaceSelected={teethAndSurfacesObject[tooth]?.has(
                                        currentTeethToSurfacesIds[tooth][7],
                                    )}
                                    isForLowerTeeth
                                    selectedMultiTeeth={selectedMultiTeeth}
                                    selectMultiTeeth={selectMultiTeeth}
                                    isSelectedMultiTeethArray={isSelectedMultiTeethArray}
                                    showRightBorder={index === lowerTeethToShow.length / 2 - 1}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {isNoTeethFound && <p className="py-8 pl-4">{chartType && 'No teeth found!'}</p>}
            </Fragment>
        );
    }
}

export default connect((state: GlobalState) => ({
    dentalExaminations: getDentalExaminations(state),
    chartType: getChartType(state),
    processedDentalExaminations: getProcessedDentalExaminations(state),
}))(Chart);
